import * as React from "react";
import { useEffect, useReducer } from "react";
import { CreateAccount } from "./CreateAccount";
import { errorMessages, ErrorPage, ErrorPageErrorCode } from "./ErrorPage";
import { InvitationExpiredPage } from "./InvitationExpired";
import { Loading } from "./Loading";
import {
  AdvarraOneLogoMashup as AdvarraLogo,
  Alert,
  Box,
  Container,
  Stack,
} from "@advarra-engineering/advarra-design-system-react";
import validateToken from "../rest/validateToken";
import {
  initialState,
  Message,
  reducer,
  State,
  ValidTokenState,
} from "../state/State";
import { ParseKeys as IntlKey } from "i18next";
import { useTranslation } from "react-i18next";
import { IconAlertOctagon } from "@tabler/icons-react";
import { showToast, ToastType } from "./Toast";

export function Application(): React.JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get(
      "invitation-token",
    );
    validateToken(token)
      .then((validity) => {
        if (validity.status === "ok" && token) {
          validity.data.invitationToken = token;
        }
        dispatch({ action: "set-token-validity", value: validity });
      })
      .catch((e) => console.error(e));
  }, []);

  /* eslint-disable */
  const handleErrorDisplay = (error: any) => {
    if (error === errorMessages.E0002) {
      showToast("Error", t(error), ToastType.ERROR);
      return null;
    }
    return <ErrorFlash error={error} />;
  };

  return (
    <Container sx={(theme) => ({ padding: theme.spacing.sm })}>
      <Stack align="center" spacing="xl">
        <Box
          sx={(theme) => ({
            marginBottom: theme.spacing.md,
            marginTop: theme.spacing.md,
          })}
        >
          <AdvarraLogo />
        </Box>
        <React.Suspense fallback={<Loading />}>
          {state.error && handleErrorDisplay(state.error)}
          <Page state={state} dispatch={dispatch} />
        </React.Suspense>
      </Stack>
    </Container>
  );
}

interface PageProps {
  state: State;
  dispatch: React.Dispatch<Message>;
}
function Page(props: PageProps): React.JSX.Element {
  const { tokenValid } = props.state;
  const { t, i18n } = useTranslation();
  const token = new URLSearchParams(window.location.search).get(
    "invitation-token",
  );

  useEffect(() => {
    document.title = t("create-account.page-title");
    document.documentElement.setAttribute("lang", i18n.language);
  }, []);

  switch (tokenValid.status) {
    case "loading":
      return <Loading />;
    case "ok":
      return (
        <CreateAccount
          state={props.state as ValidTokenState}
          dispatch={props.dispatch}
        />
      );
    case "error":
      console.error(tokenValid);
      if (tokenValid.displayToast) {
        showToast(
          "Error",
          t(errorMessages[tokenValid.code as ErrorPageErrorCode]),
          ToastType.ERROR,
        );
        return <></>;
      } else if (tokenValid.code === "E0102") {
        return <InvitationExpiredPage token={token ?? ""} />;
      } else {
        return <ErrorPage code={tokenValid.code as ErrorPageErrorCode} />;
      }
    default:
      return <Loading />;
  }
}

interface ErrorFlashProps {
  error?: IntlKey;
}
function ErrorFlash(props: ErrorFlashProps) {
  const { t } = useTranslation();
  if (props.error === undefined) {
    return <></>;
  }
  return (
    <Alert
      color="red"
      icon={<IconAlertOctagon />}
      sx={{ width: "100%" }}
      data-testid="error-flash"
    >
      {t(props.error)}
    </Alert>
  );
}
